import { useEffect, useState } from "react";
import { X } from "lucide-react";
import Coin from "./Coin";
import { YOGA_REWARD } from "../data/playtime";
import sfx from "../utils/sound";

const POSES = [
  { emoji: "🧍", name: "Mountain Pose", cue: "Stand tall & steady" },
  { emoji: "🌳", name: "Tree Pose", cue: "Balance on one foot" },
  { emoji: "🤸", name: "Star Reach", cue: "Reach for the sky" },
  { emoji: "🦋", name: "Butterfly", cue: "Sit & flap those knees" },
];
const POSE_SECS = 8;

const YogaBreak = ({ open, onComplete, onSkip }) => {
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(POSE_SECS);

  useEffect(() => {
    if (!open) {
      setIdx(0);
      setRemaining(POSE_SECS);
      return;
    }
    sfx.pop();
    const start = performance.now();
    let raf;
    const totalSecs = POSES.length * POSE_SECS;
    const loop = () => {
      const elapsed = (performance.now() - start) / 1000;
      if (elapsed >= totalSecs) {
        sfx.fanfare();
        onComplete();
        return;
      }
      const i = Math.min(POSES.length - 1, Math.floor(elapsed / POSE_SECS));
      setIdx(i);
      setRemaining(POSE_SECS - (elapsed % POSE_SECS));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const pose = POSES[idx];
  const progress = ((POSE_SECS - remaining) / POSE_SECS) * 100;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "linear-gradient(135deg,#F368E0 0%,#FFB400 100%)" }}
      data-testid="yoga-break"
    >
      <button
        onClick={onSkip}
        className="absolute top-4 right-4 rounded-full flex items-center justify-center"
        style={{ width: 44, height: 44, background: "#fff", border: "3px solid #2b2b2b", boxShadow: "0 3px 0 #2b2b2b" }}
        aria-label="Close"
        data-testid="yoga-skip-btn"
      >
        <X size={20} strokeWidth={3} />
      </button>

      <div className="text-center px-6 max-w-md">
        <div
          className="mx-auto rounded-full flex items-center justify-center bob"
          style={{
            width: 200,
            height: 200,
            background: "#FFF9E6",
            border: "5px solid #2b2b2b",
            boxShadow: "0 6px 0 #2b2b2b",
          }}
          aria-hidden="true"
          data-testid="yoga-pose-emoji"
        >
          <span style={{ fontSize: 120 }}>{pose.emoji}</span>
        </div>
        <div className="font-display text-3xl mt-5 text-white" style={{ textShadow: "0 3px 0 #2b2b2b" }} data-testid="yoga-pose-name">
          {pose.name}
        </div>
        <p className="mt-2 text-white/95 text-base">{pose.cue}</p>
        <div
          className="mt-4 rounded-full overflow-hidden mx-auto"
          style={{ width: "100%", maxWidth: 280, height: 20, background: "#fff", border: "3px solid #2b2b2b" }}
        >
          <div
            data-testid="yoga-pose-progress"
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "linear-gradient(90deg,#54D62C,#00A699)",
              transition: "width 0.1s linear",
            }}
          />
        </div>
        <p className="mt-2 text-white/80 text-sm">
          Pose {idx + 1} of {POSES.length} · {Math.ceil(remaining)}s
        </p>
        <div className="inline-flex items-center gap-2 mt-4 rounded-full px-4 py-1 font-display text-sm"
          style={{ background: "#54D62C", color: "#2b2b2b", border: "3px solid #2b2b2b" }}
        >
          <Coin size={16} /> +{YOGA_REWARD} for finishing
        </div>
      </div>
    </div>
  );
};

export default YogaBreak;
